

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
const API_BASE =
  process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";

const BRAND = {
  blue: "#0B4F8A",
  blue2: "#083B68",
  orange: "#F97316",
  orange2: "#FB923C",
  bg: "#F6F8FC",
  card: "#FFFFFF",
  text: "#172033",
  muted: "#64748B",
  border: "rgba(15, 23, 42, 0.10)",
  softBlue: "rgba(11, 79, 138, 0.08)",
  softOrange: "rgba(249, 115, 22, 0.10)",
  green: "#16A34A",
  red: "#DC2626",
  amber: "#D97706",
};


function RGrid({ children, spacing = 1.3, sx = {}, ...props }) {
  return (
    <Box
      {...props}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        gap: (theme) => theme.spacing(spacing),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function RCol({ children, xs = 12, sm, md, lg, xl, sx = {}, ...props }) {
  const span = (value) => {
    const n = Math.max(1, Math.min(12, Number(value || 12)));
    return `span ${n} / span ${n}`;
  };

  return (
    <Box
      {...props}
      sx={{
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        gridColumn: {
          xs: span(xs),
          sm: span(sm ?? xs),
          md: span(md ?? sm ?? xs),
          lg: span(lg ?? md ?? sm ?? xs),
          xl: span(xl ?? lg ?? md ?? sm ?? xs),
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Grid({
  container = false,
  item = false,
  xs = 12,
  sm,
  md,
  lg,
  xl,
  spacing = 0,
  rowSpacing,
  columnSpacing,
  alignItems,
  justifyContent,
  children,
  sx = {},
  ...props
}) {
  const makeSpan = (value) => {
    const n = Math.max(1, Math.min(12, Number(value || 12)));
    return `span ${n} / span ${n}`;
  };

  if (container) {
    const gap = spacing || 0;
    const rowGap = rowSpacing ?? gap;
    const colGap = columnSpacing ?? gap;

    return (
      <Box
        {...props}
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          rowGap: (theme) =>
            typeof rowGap === "number" ? theme.spacing(rowGap) : rowGap,
          columnGap: (theme) =>
            typeof colGap === "number" ? theme.spacing(colGap) : colGap,
          alignItems,
          justifyContent,
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      {...props}
      sx={{
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        gridColumn: {
          xs: makeSpan(xs),
          sm: makeSpan(sm ?? xs),
          md: makeSpan(md ?? sm ?? xs),
          lg: makeSpan(lg ?? md ?? sm ?? xs),
          xl: makeSpan(xl ?? lg ?? md ?? sm ?? xs),
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const LANGS = ["tr", "en", "de"];

const DICT = {
  tr: {
    title: "Global Restoran Entegrasyon Paneli",
    shortTitle: "Global Entegrasyon",
    subtitle:
      "Yemeksepeti, Migros, Trendyol, Uber Eats, Wolt, Just Eat ve özel API bağlantılarını tek panelden yönetin.",
    overview: "Genel Bakış",
    connections: "Bağlantılar",
    orders: "Dış Siparişler",
    mappings: "Ürün Eşleştirme",
    reports: "Gün Sonu Raporu",
    logsHelp: "Loglar & Yardım",
    activeConnections: "Aktif Bağlantılar",
    todayExternalOrders: "Bugünkü Dış Sipariş",
    todayExternalRevenue: "Bugünkü Dış Ciro",
    unmappedProducts: "Eşleşmeyen Ürün",
    errors: "Hata",
    refresh: "Yenile",
    addConnection: "Yeni Bağlantı",
    edit: "Düzenle",
    save: "Kaydet",
    cancel: "Vazgeç",
    close: "Kapat",
    passive: "Pasif",
    active: "Aktif",
    inactive: "Pasif",
    test: "Bağlantıyı Test Et",
    disable: "Pasife Al",
    country: "Ülke",
    currency: "Para Birimi",
    platform: "Platform",
    status: "Durum",
    accountName: "Bağlantı Adı",
    apiSettings: "API Bilgileri",
    syncSettings: "Senkronizasyon Ayarları",
    ordersSync: "Siparişleri çek",
    menuSync: "Menü gönder",
    pricesSync: "Fiyat gönder",
    stockSync: "Stok gönder",
    cashSync: "Kasa’ya işle",
    feePercent: "Komisyon %",
    noData: "Henüz kayıt yok",
    providerSelect: "Platform seç",
    countrySelect: "Ülke seç",
    allCountries: "Tüm ülkeler",
    allPlatforms: "Tüm platformlar",
    allStatuses: "Tüm durumlar",
    addManualOrder: "Manuel Dış Sipariş Ekle",
    orderNo: "Sipariş No",
    externalOrderId: "Dış Sipariş ID",
    customerName: "Müşteri Adı",
    grossAmount: "Brüt Tutar",
    commissionAmount: "Komisyon",
    discountAmount: "İndirim",
    netAmount: "Net Tutar",
    paymentMethod: "Ödeme Tipi",
    deliveryType: "Teslimat Tipi",
    orderDate: "Sipariş Tarihi",
    items: "Ürünler",
    itemHelp: "Her satır: Ürün Adı | Adet | Birim Fiyat",
    new: "Yeni",
    completed: "Tamamlandı",
    cancelled: "İptal",
    pending: "Bekliyor",
    updateStatus: "Durum Güncelle",
    addMapping: "Eşleştirme Ekle",
    externalProductName: "Platformdaki Ürün Adı",
    externalProductId: "Platform Ürün ID",
    externalVariantName: "Varyant / Seçenek",
    localProductId: "Bizim Ürün ID",
    localProductName: "Bizim Ürün Adı",
    platformPrice: "Platform Fiyatı",
    search: "Ara",
    dateFrom: "Başlangıç",
    dateTo: "Bitiş",
    exportCsv: "CSV İndir",
    reportSummary: "Rapor Özeti",
    dailyBreakdown: "Günlük Dağılım",
    orderCount: "Sipariş",
    grossTotal: "Brüt",
    commissionTotal: "Komisyon",
    netTotal: "Net",
    discountTotal: "İndirim",
    logType: "Tip",
    action: "İşlem",
    message: "Mesaj",
    helpTitle: "Bu bölüm nasıl çalışır?",
    help1Title: "1. API bilgisi nereden girilir?",
    help1:
      "Platformdan alınan API Key, Client ID, Client Secret, Store ID veya Restaurant ID bilgileri Bağlantılar sekmesinden kaydedilir.",
    help2Title: "2. Ürün eşleştirme neden gerekli?",
    help2:
      "Platformdaki ürün adı ile sizin paneldeki ürün adı farklı olabilir. Eşleştirme yapılırsa sipariş gelince stok ve kasa doğru çalışır.",
    help3Title: "3. Gün sonu raporu ne gösterir?",
    help3:
      "Her platform için brüt satış, komisyon, net alacak, sipariş sayısı ve iptalleri ülke/para birimi bazında gösterir.",
    help4Title: "4. Stok ve kasa nasıl yansır?",
    help4:
      "Sipariş geldiğinde platform kaynağıyla kasa kaydı oluşturulur. Ürün eşleşmişse stok düşümü güvenli şekilde yapılabilir.",
    saved: "Kaydedildi",
    updated: "Güncellendi",
    deleted: "Pasife alındı",
    errorGeneric: "İşlem sırasında hata oluştu",
    connectionOk: "Bağlantı ayarları doğru görünüyor",
    missingApi: "Eksik API alanları var",
    loading: "Yükleniyor...",
    mobileHint: "Mobil kart görünümü aktif. Menüleri sağa-sola kaydırarak kullanabilirsiniz.",
  },

  en: {
    title: "Global Restaurant Integration Panel",
    shortTitle: "Global Integrations",
    subtitle:
      "Manage Yemeksepeti, Migros, Trendyol, Uber Eats, Wolt, Just Eat and custom API connections from one panel.",
    overview: "Overview",
    connections: "Connections",
    orders: "External Orders",
    mappings: "Product Mapping",
    reports: "End of Day Report",
    logsHelp: "Logs & Help",
    activeConnections: "Active Connections",
    todayExternalOrders: "Today's External Orders",
    todayExternalRevenue: "Today's External Revenue",
    unmappedProducts: "Unmapped Products",
    errors: "Errors",
    refresh: "Refresh",
    addConnection: "New Connection",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    passive: "Passive",
    active: "Active",
    inactive: "Inactive",
    test: "Test Connection",
    disable: "Disable",
    country: "Country",
    currency: "Currency",
    platform: "Platform",
    status: "Status",
    accountName: "Connection Name",
    apiSettings: "API Settings",
    syncSettings: "Sync Settings",
    ordersSync: "Fetch orders",
    menuSync: "Push menu",
    pricesSync: "Push prices",
    stockSync: "Push stock",
    cashSync: "Process cash",
    feePercent: "Fee %",
    noData: "No records yet",
    providerSelect: "Select provider",
    countrySelect: "Select country",
    allCountries: "All countries",
    allPlatforms: "All platforms",
    allStatuses: "All statuses",
    addManualOrder: "Add Manual External Order",
    orderNo: "Order No",
    externalOrderId: "External Order ID",
    customerName: "Customer Name",
    grossAmount: "Gross Amount",
    commissionAmount: "Commission",
    discountAmount: "Discount",
    netAmount: "Net Amount",
    paymentMethod: "Payment Method",
    deliveryType: "Delivery Type",
    orderDate: "Order Date",
    items: "Items",
    itemHelp: "Each line: Product Name | Quantity | Unit Price",
    new: "New",
    completed: "Completed",
    cancelled: "Cancelled",
    pending: "Pending",
    updateStatus: "Update Status",
    addMapping: "Add Mapping",
    externalProductName: "External Product Name",
    externalProductId: "External Product ID",
    externalVariantName: "Variant / Option",
    localProductId: "Local Product ID",
    localProductName: "Local Product Name",
    platformPrice: "Platform Price",
    search: "Search",
    dateFrom: "Date From",
    dateTo: "Date To",
    exportCsv: "Export CSV",
    reportSummary: "Report Summary",
    dailyBreakdown: "Daily Breakdown",
    orderCount: "Orders",
    grossTotal: "Gross",
    commissionTotal: "Commission",
    netTotal: "Net",
    discountTotal: "Discount",
    logType: "Type",
    action: "Action",
    message: "Message",
    helpTitle: "How does this module work?",
    help1Title: "1. Where do I enter API credentials?",
    help1:
      "API Key, Client ID, Client Secret, Store ID or Restaurant ID values from the platform are saved under Connections.",
    help2Title: "2. Why is product mapping required?",
    help2:
      "External product names may differ from your local products. Mapping keeps stock and cash operations accurate.",
    help3Title: "3. What does the end of day report show?",
    help3:
      "It shows gross sales, commission, net receivable, order count and cancelled orders by platform, country and currency.",
    help4Title: "4. How do stock and cash work?",
    help4:
      "When an order arrives, a platform-based cash record can be created. If products are mapped, stock can be reduced safely.",
    saved: "Saved",
    updated: "Updated",
    deleted: "Disabled",
    errorGeneric: "An error occurred",
    connectionOk: "Connection settings look correct",
    missingApi: "Missing API fields",
    loading: "Loading...",
    mobileHint: "Mobile card view is active. Swipe menus horizontally when needed.",
  },

  de: {
    title: "Globales Restaurant-Integrationspanel",
    shortTitle: "Globale Integrationen",
    subtitle:
      "Yemeksepeti, Migros, Trendyol, Uber Eats, Wolt, Just Eat und Custom API-Verbindungen zentral verwalten.",
    overview: "Übersicht",
    connections: "Verbindungen",
    orders: "Externe Bestellungen",
    mappings: "Produktzuordnung",
    reports: "Tagesabschluss",
    logsHelp: "Logs & Hilfe",
    activeConnections: "Aktive Verbindungen",
    todayExternalOrders: "Externe Bestellungen heute",
    todayExternalRevenue: "Externer Umsatz heute",
    unmappedProducts: "Nicht zugeordnet",
    errors: "Fehler",
    refresh: "Aktualisieren",
    addConnection: "Neue Verbindung",
    edit: "Bearbeiten",
    save: "Speichern",
    cancel: "Abbrechen",
    close: "Schließen",
    passive: "Passiv",
    active: "Aktiv",
    inactive: "Inaktiv",
    test: "Verbindung testen",
    disable: "Deaktivieren",
    country: "Land",
    currency: "Währung",
    platform: "Plattform",
    status: "Status",
    accountName: "Verbindungsname",
    apiSettings: "API-Daten",
    syncSettings: "Sync-Einstellungen",
    ordersSync: "Bestellungen abrufen",
    menuSync: "Menü senden",
    pricesSync: "Preise senden",
    stockSync: "Bestand senden",
    cashSync: "In Kasse buchen",
    feePercent: "Gebühr %",
    noData: "Noch keine Daten",
    providerSelect: "Plattform wählen",
    countrySelect: "Land wählen",
    allCountries: "Alle Länder",
    allPlatforms: "Alle Plattformen",
    allStatuses: "Alle Status",
    addManualOrder: "Externe Bestellung manuell hinzufügen",
    orderNo: "Bestellnummer",
    externalOrderId: "Externe Bestell-ID",
    customerName: "Kundenname",
    grossAmount: "Bruttobetrag",
    commissionAmount: "Provision",
    discountAmount: "Rabatt",
    netAmount: "Nettobetrag",
    paymentMethod: "Zahlungsart",
    deliveryType: "Lieferart",
    orderDate: "Bestelldatum",
    items: "Produkte",
    itemHelp: "Jede Zeile: Produktname | Menge | Einzelpreis",
    new: "Neu",
    completed: "Abgeschlossen",
    cancelled: "Storniert",
    pending: "Wartend",
    updateStatus: "Status aktualisieren",
    addMapping: "Zuordnung hinzufügen",
    externalProductName: "Externer Produktname",
    externalProductId: "Externe Produkt-ID",
    externalVariantName: "Variante / Option",
    localProductId: "Lokale Produkt-ID",
    localProductName: "Lokaler Produktname",
    platformPrice: "Plattformpreis",
    search: "Suchen",
    dateFrom: "Von",
    dateTo: "Bis",
    exportCsv: "CSV Export",
    reportSummary: "Berichtszusammenfassung",
    dailyBreakdown: "Tägliche Aufteilung",
    orderCount: "Bestellungen",
    grossTotal: "Brutto",
    commissionTotal: "Provision",
    netTotal: "Netto",
    discountTotal: "Rabatt",
    logType: "Typ",
    action: "Aktion",
    message: "Nachricht",
    helpTitle: "Wie funktioniert dieses Modul?",
    help1Title: "1. Wo werden API-Daten eingetragen?",
    help1:
      "API Key, Client ID, Client Secret, Store ID oder Restaurant ID werden unter Verbindungen gespeichert.",
    help2Title: "2. Warum ist Produktzuordnung nötig?",
    help2:
      "Externe Produktnamen können anders sein als lokale Produkte. Zuordnung sorgt für korrekte Kasse und Lager.",
    help3Title: "3. Was zeigt der Tagesabschluss?",
    help3:
      "Bruttoumsatz, Provision, Nettoforderung, Bestellanzahl und Stornos nach Plattform, Land und Währung.",
    help4Title: "4. Wie funktionieren Lager und Kasse?",
    help4:
      "Bei Bestelleingang kann ein Kassenbeleg mit Plattformquelle erstellt werden. Bei Zuordnung kann Lager reduziert werden.",
    saved: "Gespeichert",
    updated: "Aktualisiert",
    deleted: "Deaktiviert",
    errorGeneric: "Ein Fehler ist aufgetreten",
    connectionOk: "Verbindungseinstellungen sehen korrekt aus",
    missingApi: "API-Felder fehlen",
    loading: "Wird geladen...",
    mobileHint: "Mobile Kartenansicht ist aktiv. Menüs können seitlich gescrollt werden.",
  },
};

const COUNTRIES = [
  { code: "TR", name: "Türkiye" },
  { code: "CH", name: "İsviçre / Switzerland" },
  { code: "DE", name: "Almanya / Germany" },
  { code: "AT", name: "Avusturya / Austria" },
  { code: "FR", name: "Fransa / France" },
  { code: "NL", name: "Hollanda / Netherlands" },
  { code: "BE", name: "Belçika / Belgium" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "OTHER", name: "Diğer / Other" },
];

const CURRENCIES = ["TRY", "CHF", "EUR", "USD", "GBP"];

const STATUS_COLORS = {
  active: "success",
  inactive: "default",
  passive: "default",
  new: "warning",
  completed: "success",
  cancelled: "error",
  pending: "warning",
  ok: "success",
  error: "error",
};

const cardSx = {
  borderRadius: { xs: 3, md: 4 },
  border: `1px solid ${BRAND.border}`,
  boxShadow: "0 14px 40px rgba(15, 23, 42, 0.07)",
  overflow: "hidden",
  minWidth: 0,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    backgroundColor: "#fff",
    minWidth: 0,
  },
  "& .MuiInputBase-input": {
    minWidth: 0,
  },
};

function tokenHeader() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function safeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeObject(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function formatDate(value, lang = "tr") {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);

  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatDateTime(value, lang = "tr") {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  return d.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function money(value, currency = "TRY", lang = "tr") {
  const num = Number(value || 0);
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "TRY",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ""}`;
  }
}

function normalizeNumber(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const normalized = String(value).replace(",", ".");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
}

function getStatusLabel(status, t) {
  if (!status) return "-";
  return t(status) || status;
}

function parseItemsText(text) {
  if (!text) return [];

  return String(text)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split("|").map((x) => x.trim());
      const name = parts[0] || "Ürün";
      const quantity = normalizeNumber(parts[1] || 1);
      const unitPrice = normalizeNumber(parts[2] || 0);

      return {
        external_product_name: name,
        quantity: quantity || 1,
        unit_price: unitPrice,
        total_price: (quantity || 1) * unitPrice,
      };
    });
}

function csvEscape(value) {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function SectionTitle({ title, subtitle, right }) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", md: "center" }}
      justifyContent="space-between"
      sx={{
        mb: 2,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 950,
            color: BRAND.text,
            lineHeight: 1.18,
            fontSize: { xs: 22, md: 24 },
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>

        {subtitle ? (
          <Typography
            variant="body2"
            sx={{
              color: BRAND.muted,
              mt: 0.5,
              lineHeight: 1.55,
              maxWidth: 950,
              overflowWrap: "anywhere",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {right ? (
        <Box sx={{ flexShrink: 0, minWidth: { xs: "100%", md: "auto" } }}>
          {right}
        </Box>
      ) : null}
    </Stack>
  );
}

function MetricCard({ label, value, helper, tone = "blue", compact = false }) {  const isOrange = tone === "orange";

  return (
    <Card
      sx={{
        ...cardSx,
        height: "100%",
        minHeight: compact ? 108 : { xs: 118, md: 140 },
        background: isOrange
          ? `linear-gradient(135deg, ${BRAND.orange}, ${BRAND.orange2})`
          : `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.blue2})`,
        color: "#fff",
      }}
    >
      <CardContent
        sx={{
          p: compact ? 1.5 : { xs: 1.7, md: 2.3 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            opacity: 0.9,
            fontWeight: 900,
            mb: 1,
            lineHeight: 1.25,
            overflowWrap: "anywhere",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontWeight: 950,
            lineHeight: 1.1,
            fontSize: { xs: 30, md: 30 },
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>

        {helper ? (
          <Typography
            variant="caption"
            sx={{
              opacity: 0.9,
              mt: 0.8,
              fontWeight: 800,
              overflowWrap: "anywhere",
            }}
          >
            {helper}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function EmptyBox({ text }) {
  return (
    <Card
      sx={{
        ...cardSx,
        p: { xs: 2.5, md: 3 },
        textAlign: "center",
        background: "linear-gradient(135deg, #fff, #F8FAFC)",
      }}
    >
      <Typography sx={{ fontWeight: 900, color: BRAND.muted }}>
        {text}
      </Typography>
    </Card>
  );
}

function ResponsiveButton({ children, sx = {}, ...props }) {
  return (
    <Button
      {...props}
      sx={{
        borderRadius: 999,
        fontWeight: 950,
        minHeight: 44,
        whiteSpace: "normal",
        px: 2,
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

export default function EntegrasyonPanel() {
  const isMobile = useMediaQuery("(max-width:900px)");

  const [lang, setLang] = useState(
    localStorage.getItem("panel_lang") ||
      localStorage.getItem("app_lang") ||
      localStorage.getItem("lang") ||
      "tr"
  );

  const T = useMemo(() => DICT[lang] || DICT.tr, [lang]);
  const t = useCallback((key) => T[key] || DICT.tr[key] || key, [T]);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportSummary, setReportSummary] = useState([]);
  const [logs, setLogs] = useState([]);

  const [filters, setFilters] = useState({
    country_code: "",
    provider_key: "",
    status: "",
    q: "",
    date_from: todayISO(),
    date_to: todayISO(),
    currency: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [accountDialog, setAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    provider_id: "",
    account_name: "",
    country_code: "TR",
    currency: "TRY",
    status: "active",
    platform_fee_percent: 0,
    credentials: {},
    sync_settings: {
      orders: true,
      menu: false,
      prices: false,
      stock: false,
      cash: true,
    },
  });

  const [orderDialog, setOrderDialog] = useState(false);
  const [orderForm, setOrderForm] = useState({
    account_id: "",
    external_order_id: "",
    order_no: "",
    customer_name: "",
    gross_amount: "",
    discount_amount: "",
    commission_amount: "",
    net_amount: "",
    payment_method: "online",
    delivery_type: "delivery",
    order_date: todayISO(),
    items_text: "",
  });

  const [mappingDialog, setMappingDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [mappingForm, setMappingForm] = useState({
    account_id: "",
    external_product_id: "",
    external_product_name: "",
    external_variant_name: "",
    local_product_id: "",
    local_product_name: "",
    platform_price: "",
    currency: "TRY",
  });

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, severity, message });
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...tokenHeader(),
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.ok === false) {
      throw new Error(data.message || data.error || "API error");
    }

    return data;
  }, []);

  const loadProviders = useCallback(async () => {
    const data = await apiFetch("/entegrasyon/providers");
    setProviders(data.data || []);
  }, [apiFetch]);

  const loadAccounts = useCallback(async () => {
    const params = new URLSearchParams();

    if (filters.country_code) params.set("country_code", filters.country_code);
    if (filters.provider_key) params.set("provider_key", filters.provider_key);
    if (filters.status) params.set("status", filters.status);

    const data = await apiFetch(`/entegrasyon/accounts?${params.toString()}`);
    setAccounts(data.data || []);
  }, [apiFetch, filters.country_code, filters.provider_key, filters.status]);

  const loadOrders = useCallback(async () => {
    const params = new URLSearchParams();

    if (filters.provider_key) params.set("provider_key", filters.provider_key);
    if (filters.status) params.set("status", filters.status);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    params.set("limit", "120");

    const data = await apiFetch(`/entegrasyon/orders?${params.toString()}`);
    setOrders(data.data || []);
  }, [
    apiFetch,
    filters.provider_key,
    filters.status,
    filters.date_from,
    filters.date_to,
  ]);

  const loadMappings = useCallback(async () => {
    const params = new URLSearchParams();

    if (filters.q) params.set("q", filters.q);

    const data = await apiFetch(`/entegrasyon/mappings?${params.toString()}`);
    setMappings(data.data || []);
  }, [apiFetch, filters.q]);

  const loadReports = useCallback(async () => {
    const params = new URLSearchParams();

    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (filters.provider_key) params.set("provider_key", filters.provider_key);
    if (filters.country_code) params.set("country_code", filters.country_code);
    if (filters.currency) params.set("currency", filters.currency);

    const data = await apiFetch(
      `/entegrasyon/reports/daily?${params.toString()}`
    );

    setReports(data.data || []);
    setReportSummary(data.summary || []);
  }, [
    apiFetch,
    filters.date_from,
    filters.date_to,
    filters.provider_key,
    filters.country_code,
    filters.currency,
  ]);

  const loadLogs = useCallback(async () => {
    const params = new URLSearchParams();

    if (filters.provider_key) params.set("provider_key", filters.provider_key);
    if (filters.status) params.set("status", filters.status);
    params.set("limit", "80");

    const data = await apiFetch(`/entegrasyon/logs?${params.toString()}`);
    setLogs(data.data || []);
  }, [apiFetch, filters.provider_key, filters.status]);

  const loadAll = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadProviders(),
        loadAccounts(),
        loadOrders(),
        loadMappings(),
        loadReports(),
        loadLogs(),
      ]);
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    } finally {
      setLoading(false);
    }
  }, [
    loadProviders,
    loadAccounts,
    loadOrders,
    loadMappings,
    loadReports,
    loadLogs,
    showSnack,
    t,
  ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  function changeLang(nextLang) {
    setLang(nextLang);
    localStorage.setItem("panel_lang", nextLang);
    localStorage.setItem("app_lang", nextLang);
    window.dispatchEvent(new Event("app_lang_change"));
  }

  const selectedProvider = useMemo(() => {
    return providers.find(
      (provider) => String(provider.id) === String(accountForm.provider_id)
    );
  }, [providers, accountForm.provider_id]);

  const selectedProviderFields = useMemo(() => {
    return safeArray(selectedProvider?.required_fields);
  }, [selectedProvider]);

  const activeAccounts = accounts.filter((account) => account.status === "active");

  const unmappedCount = mappings.filter(
    (mapping) => !mapping.local_product_id && mapping.is_active !== false
  ).length;

  const errorCount = logs.filter((log) => log.status === "error").length;

  const todayReportSummary = reportSummary.reduce(
    (acc, row) => {
      acc.order_count += Number(row.order_count || 0);
      acc.gross_total += Number(row.gross_total || 0);
      acc.currency = row.currency || acc.currency || "TRY";
      return acc;
    },
    { order_count: 0, gross_total: 0, currency: "TRY" }
  );

  function resetAccountForm() {
    setEditingAccount(null);
    setAccountForm({
      provider_id: "",
      account_name: "",
      country_code: "TR",
      currency: "TRY",
      status: "active",
      platform_fee_percent: 0,
      credentials: {},
      sync_settings: {
        orders: true,
        menu: false,
        prices: false,
        stock: false,
        cash: true,
      },
    });
  }

  function openNewAccount() {
    resetAccountForm();
    setAccountDialog(true);
  }

  function openEditAccount(account) {
    setEditingAccount(account);

    setAccountForm({
      provider_id: account.provider_id || "",
      account_name: account.account_name || "",
      country_code: account.country_code || "TR",
      currency: account.currency || "TRY",
      status: account.status || "active",
      platform_fee_percent: account.platform_fee_percent || 0,
      credentials: account.credentials_masked || {},
      sync_settings: safeObject(account.sync_settings, {
        orders: true,
        menu: false,
        prices: false,
        stock: false,
        cash: true,
      }),
    });

    setAccountDialog(true);
  }

  async function saveAccount() {
    try {
      if (!accountForm.provider_id || !accountForm.account_name) {
        showSnack(t("providerSelect"), "warning");
        return;
      }

      const credentials = { ...(accountForm.credentials || {}) };

      Object.keys(credentials).forEach((key) => {
        if (String(credentials[key] || "").includes("***")) {
          delete credentials[key];
        }
      });

      const body = {
        provider_id: Number(accountForm.provider_id),
        account_name: accountForm.account_name,
        country_code: accountForm.country_code,
        currency: accountForm.currency,
        status: accountForm.status,
        platform_fee_percent: normalizeNumber(accountForm.platform_fee_percent),
        public_settings: {},
        sync_settings: accountForm.sync_settings,
      };

      if (Object.keys(credentials).length > 0 || !editingAccount) {
        body.credentials = credentials;
      }

      if (editingAccount) {
        await apiFetch(`/entegrasyon/accounts/${editingAccount.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        showSnack(t("updated"));
      } else {
        await apiFetch("/entegrasyon/accounts", {
          method: "POST",
          body: JSON.stringify(body),
        });
        showSnack(t("saved"));
      }

      setAccountDialog(false);
      resetAccountForm();
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  async function testAccount(account) {
    try {
      const data = await apiFetch(`/entegrasyon/accounts/${account.id}/test`, {
        method: "POST",
        body: JSON.stringify({}),
      });

      showSnack(data.message || t("connectionOk"));
      await loadLogs();
    } catch (err) {
      showSnack(err.message || t("missingApi"), "error");
      await loadLogs();
    }
  }

  async function disableAccount(account) {
    try {
      await apiFetch(`/entegrasyon/accounts/${account.id}`, {
        method: "DELETE",
      });

      showSnack(t("deleted"));
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  function resetOrderForm() {
    setOrderForm({
      account_id: activeAccounts[0]?.id || "",
      external_order_id: "",
      order_no: "",
      customer_name: "",
      gross_amount: "",
      discount_amount: "",
      commission_amount: "",
      net_amount: "",
      payment_method: "online",
      delivery_type: "delivery",
      order_date: todayISO(),
      items_text: "",
    });
  }

  function openNewOrder() {
    resetOrderForm();
    setOrderDialog(true);
  }

  async function saveManualOrder() {
    try {
      if (!orderForm.account_id || !orderForm.external_order_id) {
        showSnack(t("externalOrderId"), "warning");
        return;
      }

      const account = accounts.find(
        (item) => String(item.id) === String(orderForm.account_id)
      );

      const gross = normalizeNumber(orderForm.gross_amount);
      const discount = normalizeNumber(orderForm.discount_amount);
      const commission = normalizeNumber(orderForm.commission_amount);
      const net =
        orderForm.net_amount === ""
          ? gross - discount - commission
          : normalizeNumber(orderForm.net_amount);

      await apiFetch("/entegrasyon/orders/manual", {
        method: "POST",
        body: JSON.stringify({
          account_id: Number(orderForm.account_id),
          external_order_id: orderForm.external_order_id,
          order_no: orderForm.order_no,
          order_status: "new",
          payment_status: "pending",
          payment_method: orderForm.payment_method,
          delivery_type: orderForm.delivery_type,
          country_code: account?.country_code || "TR",
          currency: account?.currency || "TRY",
          gross_amount: gross,
          discount_amount: discount,
          commission_amount: commission,
          net_amount: net,
          customer_json: {
            name: orderForm.customer_name,
          },
          order_date: orderForm.order_date,
          items: parseItemsText(orderForm.items_text),
        }),
      });

      showSnack(t("saved"));
      setOrderDialog(false);
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  async function updateOrderStatus(order, nextStatus) {
    try {
      await apiFetch(`/entegrasyon/orders/${order.id}/status`, {
        method: "PUT",
        body: JSON.stringify({
          order_status: nextStatus,
        }),
      });

      showSnack(t("updated"));
      await loadOrders();
      await loadReports();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  function resetMappingForm() {
    setEditingMapping(null);
    setMappingForm({
      account_id: activeAccounts[0]?.id || "",
      external_product_id: "",
      external_product_name: "",
      external_variant_name: "",
      local_product_id: "",
      local_product_name: "",
      platform_price: "",
      currency: activeAccounts[0]?.currency || "TRY",
    });
  }

  function openNewMapping() {
    resetMappingForm();
    setMappingDialog(true);
  }

  function openEditMapping(mapping) {
    setEditingMapping(mapping);
    setMappingForm({
      account_id: mapping.account_id || "",
      external_product_id: mapping.external_product_id || "",
      external_product_name: mapping.external_product_name || "",
      external_variant_name: mapping.external_variant_name || "",
      local_product_id: mapping.local_product_id || "",
      local_product_name: mapping.local_product_name || "",
      platform_price: mapping.platform_price || "",
      currency: mapping.currency || "TRY",
    });
    setMappingDialog(true);
  }

  async function saveMapping() {
    try {
      if (!mappingForm.account_id || !mappingForm.external_product_name) {
        showSnack(t("externalProductName"), "warning");
        return;
      }

      const body = {
        account_id: Number(mappingForm.account_id),
        external_product_id: mappingForm.external_product_id || null,
        external_product_name: mappingForm.external_product_name,
        external_variant_name: mappingForm.external_variant_name || null,
        local_product_id: mappingForm.local_product_id
          ? Number(mappingForm.local_product_id)
          : null,
        local_product_name: mappingForm.local_product_name || null,
        platform_price:
          mappingForm.platform_price === ""
            ? null
            : normalizeNumber(mappingForm.platform_price),
        currency: mappingForm.currency || "TRY",
      };

      if (editingMapping) {
        await apiFetch(`/entegrasyon/mappings/${editingMapping.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        showSnack(t("updated"));
      } else {
        await apiFetch("/entegrasyon/mappings", {
          method: "POST",
          body: JSON.stringify(body),
        });
        showSnack(t("saved"));
      }

      setMappingDialog(false);
      resetMappingForm();
      await loadAll();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  async function disableMapping(mapping) {
    try {
      await apiFetch(`/entegrasyon/mappings/${mapping.id}`, {
        method: "DELETE",
      });

      showSnack(t("deleted"));
      await loadMappings();
    } catch (err) {
      showSnack(err.message || t("errorGeneric"), "error");
    }
  }

  function exportReportsCsv() {
    const rows = [
      [
        "date",
        "provider",
        "country",
        "currency",
        "orders",
        "gross",
        "discount",
        "commission",
        "net",
      ],
      ...reports.map((row) => [
        row.report_date,
        row.provider_name || row.provider_key,
        row.country_code,
        row.currency,
        row.order_count,
        row.gross_total,
        row.discount_total,
        row.commission_total,
        row.net_total,
      ]),
    ];

    const csv = rows
      .map((row) => row.map((cell) => csvEscape(cell)).join(";"))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `entegrasyon-gun-sonu-${todayISO()}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  }

  const filterBar = (
    <Card
      sx={{
        ...cardSx,
        mb: 2,
        background: "rgba(255,255,255,0.92)",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.2 } }}>
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6} lg={3}>
  <TextField
    fullWidth
    size="small"
    type="date"
    label={t("dateFrom")}
              value={filters.date_from}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_from: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
  <TextField
    fullWidth
    size="small"
    type="date"
    label={t("dateTo")}
              value={filters.date_to}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_to: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={2}>
  <TextField
    select
    fullWidth
    size="small"
    label={t("country")}
              value={filters.country_code}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, country_code: e.target.value }))
              }
              sx={inputSx}
            >
              <MenuItem value="">{t("allCountries")}</MenuItem>
              {COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.code} - {country.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} lg={2}>
  <TextField
    select
    fullWidth
    size="small"
    label={t("platform")}
              value={filters.provider_key}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, provider_key: e.target.value }))
              }
              sx={inputSx}
            >
              <MenuItem value="">{t("allPlatforms")}</MenuItem>
              {providers.map((provider) => (
                <MenuItem
                  key={provider.provider_key}
                  value={provider.provider_key}
                >
                  {provider.provider_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} lg={2}>
  <TextField
    select
    fullWidth
    size="small"
    label={t("status")}
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
              sx={inputSx}
            >
              <MenuItem value="">{t("allStatuses")}</MenuItem>
              <MenuItem value="active">{t("active")}</MenuItem>
              <MenuItem value="inactive">{t("inactive")}</MenuItem>
              <MenuItem value="new">{t("new")}</MenuItem>
              <MenuItem value="completed">{t("completed")}</MenuItem>
              <MenuItem value="cancelled">{t("cancelled")}</MenuItem>
              <MenuItem value="error">{t("errors")}</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const titleText =
    isMobile && lang === "tr"
      ? "Global Entegrasyon"
      : isMobile && lang === "en"
      ? "Global Integrations"
      : isMobile && lang === "de"
      ? "Globale Integrationen"
      : t("title");

  const subtitleText =
    isMobile && lang === "tr"
      ? "API bağlantıları, dış siparişler, ürün eşleştirme ve gün sonu raporları tek ekranda."
      : isMobile && lang === "en"
      ? "API connections, external orders, product mapping and reports in one panel."
      : isMobile && lang === "de"
      ? "API-Verbindungen, externe Bestellungen, Produktzuordnung und Berichte in einem Panel."
      : t("subtitle");


 return (
  <Box
    sx={{
      width: { xs: "100vw", md: "100%" },
      maxWidth: { xs: "100vw", md: "100%" },
      minWidth: 0,
      minHeight: "100vh",
      overflowX: "clip",

      // Mobilde React Admin alanı 900px gibi kalırsa bunu kırar.
      ml: { xs: "calc(50% - 50vw)", md: 0 },
      mr: { xs: "calc(50% - 50vw)", md: 0 },

      background:
        "radial-gradient(circle at top left, rgba(249,115,22,0.12), transparent 30%), radial-gradient(circle at top right, rgba(11,79,138,0.14), transparent 34%), linear-gradient(145deg, #F8FAFC 0%, #EEF2F7 100%)",
      boxSizing: "border-box",
p: { xs: 2, sm: 2, md: 2.5 },
pb: { xs: 5, md: 7 },

      "& *": {
        boxSizing: "border-box",
      },
      "& .MuiCard-root": {
  minWidth: 0,
  maxWidth: "100%",
},

"& .entegrasyon-shell .MuiCardContent-root": {
  paddingLeft: { xs: "24px !important", md: "24px !important" },
  paddingRight: { xs: "24px !important", md: "24px !important" },
},

"& .MuiTypography-root": {
  minWidth: 0,
  maxWidth: "100%",
},
      "& .MuiButton-root": {
        textTransform: "none",
      },
      "& .MuiDialog-paper": {
        width: { xs: "calc(100vw - 20px)", sm: "calc(100% - 64px)" },
        maxWidth: "920px",
        m: { xs: 1, sm: 2 },
      },


              }}
    >
      <Box
        className="entegrasyon-shell"
        sx={{
          width: "100%",
          maxWidth: 1580,
          mx: "auto",
          minWidth: 0,
          overflowX: "clip",
        }}
      >
        <Card
          className="entegrasyon-hero"
          sx={{
            ...cardSx,
            mb: { xs: 1.5, md: 2 },
            background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.blue2})`,
            color: "#fff",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.4, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1.5, md: 2 }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Chip
                  label="🌍 GLOBAL API"
                  sx={{
                    mb: 1.2,
                    bgcolor: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    fontWeight: 950,
                    border: "1px solid rgba(255,255,255,0.22)",
                    maxWidth: "100%",
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 950,
                    lineHeight: 1.1,
                    fontSize: { xs: 28, sm: 34, md: 38 },
                    letterSpacing: "-0.03em",
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                  }}
                >
               {titleText}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    opacity: 0.88,
                    maxWidth: 980,
                    lineHeight: 1.55,
                    fontSize: { xs: 15, md: 16 },
                    overflowWrap: "anywhere",
                  }}
                >
                  {subtitleText}
                </Typography>

                <Alert
                  severity="info"
                  sx={{
                    mt: 1.5,
                    display: { xs: "flex", md: "none" },
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "& .MuiAlert-icon": { color: "#fff" },
                    "& .MuiAlert-message": {
                      fontWeight: 800,
                      overflowWrap: "anywhere",
                    },
                  }}
                >
                  {t("mobileHint")}
                </Alert>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: "100%", md: "auto" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.8}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    bgcolor: "rgba(255,255,255,0.10)",
                    p: 0.6,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.18)",
                    justifyContent: "center",
                    overflowX: "auto",
                  }}
                >
                  {LANGS.map((item) => (
                    <Button
                      key={item}
                      size="small"
                      onClick={() => changeLang(item)}
                      sx={{
                        minWidth: 46,
                        borderRadius: 999,
                        fontWeight: 950,
                        color: lang === item ? BRAND.blue : "#fff",
                        bgcolor: lang === item ? "#fff" : "transparent",
                        "&:hover": {
                          bgcolor:
                            lang === item ? "#fff" : "rgba(255,255,255,0.14)",
                        },
                      }}
                    >
                      {item.toUpperCase()}
                    </Button>
                  ))}
                </Stack>

                <ResponsiveButton
                  onClick={loadAll}
                  disabled={loading}
                  variant="contained"
                  sx={{
                    minWidth: { xs: "100%", sm: 120 },
                    bgcolor: BRAND.orange,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    t("refresh")
                  )}
                </ResponsiveButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <RGrid spacing={1.3} sx={{ mb: { xs: 1.5, md: 2 } }}>
  <RCol xs={12} sm={6} lg={3}>
    <MetricCard
      compact={isMobile}
      label={t("activeConnections")}
      value={activeAccounts.length}
      helper={`${accounts.length} ${t("connections")}`}
    />
  </RCol>

  <RCol xs={12} sm={6} lg={3}>
    <MetricCard
      compact={isMobile}
      label={t("todayExternalOrders")}
      value={todayReportSummary.order_count}
      helper={formatDate(todayISO(), lang)}
      tone="orange"
    />
  </RCol>

  <RCol xs={12} sm={6} lg={3}>
    <MetricCard
      compact={isMobile}
      label={t("todayExternalRevenue")}
      value={money(
        todayReportSummary.gross_total,
        todayReportSummary.currency,
        lang
      )}
      helper={t("grossTotal")}
    />
  </RCol>

  <RCol xs={12} sm={6} lg={3}>
    <MetricCard
      compact={isMobile}
      label={t("unmappedProducts")}
      value={unmappedCount}
      helper={`${errorCount} ${t("errors")}`}
      tone="orange"
    />
  </RCol>
</RGrid>

        <Card
          className="entegrasyon-tabs-card"
          sx={{
  ...cardSx,
  mb: { xs: 1.5, md: 2 },
  background: "rgba(255,255,255,0.94)",
}}
        >
          <Tabs
            value={tab}
            onChange={(event, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: { xs: 0.5, md: 1.2 },
              maxWidth: "100%",
              overflowX: "auto",
              "& .MuiTabs-scroller": {
                overflowX: "auto !important",
              },
              "& .MuiTab-root": {
                minHeight: { xs: 56, md: 60 },
                minWidth: { xs: 142, sm: 160, md: 180 },
                px: { xs: 1.2, md: 2 },
                fontWeight: 950,
                textTransform: "none",
                color: BRAND.muted,
                whiteSpace: "nowrap",
                fontSize: { xs: "0.84rem", md: "0.92rem" },
              },
              "& .Mui-selected": {
                color: `${BRAND.orange} !important`,
              },
              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: 999,
                bgcolor: BRAND.orange,
              },
            }}
          >
            <Tab label={`📊 ${t("overview")}`} />
            <Tab label={`🔌 ${t("connections")}`} />
            <Tab label={`🧾 ${t("orders")}`} />
            <Tab label={`🔁 ${t("mappings")}`} />
            <Tab label={`📈 ${t("reports")}`} />
            <Tab label={`🛠 ${t("logsHelp")}`} />
          </Tabs>
        </Card>

        <Collapse in={tab === 0} unmountOnExit>
          <SectionTitle
            title={t("overview")}
            subtitle="Global altyapı: ülke, platform, para birimi, API ve sipariş akışı tek merkezde."
            right={
              <ResponsiveButton
                fullWidth
                variant="contained"
                onClick={openNewAccount}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                }}
              >
                + {t("addConnection")}
              </ResponsiveButton>
            }
          />

          <Grid container spacing={1.5}>
            {providers.map((provider) => {
              const connected = accounts.filter(
                (account) => account.provider_key === provider.provider_key
              ).length;

              const countries = safeArray(provider.countries);

              return (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={provider.id}>
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                      <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          alignItems="flex-start"
                          sx={{ minWidth: 0 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                lineHeight: 1.2,
                                overflowWrap: "anywhere",
                                fontSize: { xs: 18, md: 17 },
                              }}
                            >
                              {provider.provider_name}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 800,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {provider.provider_key}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={provider.is_global ? "GLOBAL" : "LOCAL"}
                            sx={{
                              flexShrink: 0,
                              fontWeight: 950,
                              bgcolor: provider.is_global
                                ? BRAND.softBlue
                                : BRAND.softOrange,
                              color: provider.is_global
                                ? BRAND.blue
                                : BRAND.orange,
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.7}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ minWidth: 0 }}
                        >
                          {countries.slice(0, 5).map((country) => (
                            <Chip
                              key={country}
                              size="small"
                              label={country}
                              variant="outlined"
                              sx={{ fontWeight: 850 }}
                            />
                          ))}

                          {countries.length > 5 ? (
                            <Chip
                              size="small"
                              label={`+${countries.length - 5}`}
                              variant="outlined"
                              sx={{ fontWeight: 850 }}
                            />
                          ) : null}
                        </Stack>

                        <Divider />

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: BRAND.muted, fontWeight: 850 }}
                          >
                            {t("connections")}
                          </Typography>

                          <Typography sx={{ fontWeight: 950, color: BRAND.blue }}>
                            {connected}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Collapse>

        <Collapse in={tab === 1} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("connections")}
            subtitle="Her ülke ve platform için API bilgilerini güvenli şekilde kaydedin."
            right={
              <ResponsiveButton
                fullWidth
                variant="contained"
                onClick={openNewAccount}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                }}
              >
                + {t("addConnection")}
              </ResponsiveButton>
            }
          />

          {accounts.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {accounts.map((account) => (
                <Grid item xs={12} md={6} xl={4} key={account.id}>
                  <Card sx={{ ...cardSx, 
height: "100%" }}>
                    <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                      <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "flex-start" }}
                          sx={{ minWidth: 0 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                lineHeight: 1.2,
                                overflowWrap: "anywhere",
                                fontSize: { xs: 18, md: 18 },
                              }}
                            >
                              {account.account_name}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 850,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {account.provider_name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={getStatusLabel(account.status, t)}
                            color={STATUS_COLORS[account.status] || "default"}
                            sx={{
                              fontWeight: 950,
                              alignSelf: { xs: "flex-start", sm: "center" },
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.8}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip size="small" label={account.country_code} />
                          <Chip size="small" label={account.currency} />
                          <Chip
                            size="small"
                            label={`${t("feePercent")}: ${
                              account.platform_fee_percent || 0
                            }`}
                          />
                        </Stack>

                        <Box
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            border: `1px solid ${BRAND.border}`,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 950 }}
                          >
                            {t("apiSettings")}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.7}
                            useFlexGap
                            flexWrap="wrap"
                            mt={0.8}
                            sx={{ minWidth: 0 }}
                          >
                            {Object.keys(account.credentials_masked || {}).length ===
                            0 ? (
                              <Chip
                                size="small"
                                label="No credentials"
                                variant="outlined"
                              />
                            ) : (
                              Object.entries(account.credentials_masked || {}).map(
                                ([key, value]) => (
                                  <Chip
                                    key={key}
                                    size="small"
                                    label={`${key}: ${value}`}
                                    variant="outlined"
                                    sx={{
                                      maxWidth: "100%",
                                      "& .MuiChip-label": {
                                        overflowWrap: "anywhere",
                                        whiteSpace: "normal",
                                      },
                                    }}
                                  />
                                )
                              )
                            )}
                          </Stack>
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            color: BRAND.muted,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          Last sync: {formatDateTime(account.last_order_sync_at, lang)}
                        </Typography>

                        {account.last_error ? (
                          <Alert severity="error" sx={{ borderRadius: 3 }}>
                            {account.last_error}
                          </Alert>
                        ) : null}

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <ResponsiveButton
                            fullWidth
                            variant="outlined"
                            onClick={() => testAccount(account)}
                          >
                            {t("test")}
                          </ResponsiveButton>

                          <ResponsiveButton
                            fullWidth
                            variant="contained"
                            onClick={() => openEditAccount(account)}
                            sx={{
                              bgcolor: BRAND.blue,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: BRAND.blue2,
                                boxShadow: "none",
                              },
                            }}
                          >
                            {t("edit")}
                          </ResponsiveButton>

                          <ResponsiveButton
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={() => disableAccount(account)}
                          >
                            {t("disable")}
                          </ResponsiveButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 2} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("orders")}
            subtitle="Platformlardan gelen siparişleri tek ekranda takip edin."
            right={
              <ResponsiveButton
                fullWidth
                variant="contained"
                onClick={openNewOrder}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                }}
              >
                + {t("addManualOrder")}
              </ResponsiveButton>
            }
          />

          {orders.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {orders.map((order) => {
                const customer = safeObject(order.customer_json, {});
                const items = safeArray(order.items);

                return (
                  <Grid item xs={12} lg={6} key={order.id}>
                    <Card sx={{ ...cardSx, height: "100%" }}>
                      <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                        <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "flex-start" }}
                            sx={{ minWidth: 0 }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: BRAND.text,
                                  lineHeight: 1.2,
                                  overflowWrap: "anywhere",
                                  fontSize: { xs: 18, md: 18 },
                                }}
                              >
                                {order.provider_name || order.provider_key} #
                                {order.order_no || order.external_order_id}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: BRAND.muted,
                                  fontWeight: 850,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {customer.name || "-"} •{" "}
                                {formatDateTime(order.order_date, lang)}
                              </Typography>
                            </Box>

                            <Chip
                              size="small"
                              label={getStatusLabel(order.order_status, t)}
                              color={STATUS_COLORS[order.order_status] || "default"}
                              sx={{
                                fontWeight: 950,
                                alignSelf: { xs: "flex-start", sm: "center" },
                              }}
                            />
                          </Stack>

                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="caption"
                                sx={{ color: BRAND.muted, fontWeight: 800 }}
                              >
                                {t("grossTotal")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {money(order.gross_amount, order.currency, lang)}
                              </Typography>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="caption"
                                sx={{ color: BRAND.muted, fontWeight: 800 }}
                              >
                                {t("commissionTotal")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: BRAND.orange,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {money(order.commission_amount, order.currency, lang)}
                              </Typography>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="caption"
                                sx={{ color: BRAND.muted, fontWeight: 800 }}
                              >
                                {t("netTotal")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: BRAND.green,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {money(order.net_amount, order.currency, lang)}
                              </Typography>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="caption"
                                sx={{ color: BRAND.muted, fontWeight: 800 }}
                              >
                                {t("currency")}
                              </Typography>
                              <Typography sx={{ fontWeight: 950 }}>
                                {order.country_code} / {order.currency}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Divider />

                          <Stack spacing={0.8}>
                            {items.length === 0 ? (
                              <Typography
                                variant="body2"
                                sx={{ color: BRAND.muted, fontWeight: 800 }}
                              >
                                {t("items")}: -
                              </Typography>
                            ) : (
                              items.map((item) => (
                                <Box
                                  key={item.id}
                                  sx={{
                                    p: 1,
                                    borderRadius: 3,
                                    bgcolor:
                                      item.mapping_status === "mapped"
                                        ? "rgba(22,163,74,0.08)"
                                        : "rgba(249,115,22,0.08)",
                                    border: `1px solid ${BRAND.border}`,
                                    minWidth: 0,
                                  }}
                                >
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    justifyContent="space-between"
                                    spacing={0.8}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 950,
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {item.quantity}x {item.external_product_name}
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 950,
                                        color: BRAND.blue,
                                      }}
                                    >
                                      {money(item.total_price, item.currency, lang)}
                                    </Typography>
                                  </Stack>

                                  <Typography
                                    variant="caption"
                                    sx={{ color: BRAND.muted, fontWeight: 800 }}
                                  >
                                    {item.mapping_status === "mapped"
                                      ? item.local_product_name || "Mapped"
                                      : t("unmappedProducts")}
                                  </Typography>
                                </Box>
                              ))
                            )}
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            <ResponsiveButton
                              fullWidth
                              variant="outlined"
                              onClick={() => updateOrderStatus(order, "completed")}
                            >
                              {t("completed")}
                            </ResponsiveButton>

                            <ResponsiveButton
                              fullWidth
                              variant="outlined"
                              color="error"
                              onClick={() => updateOrderStatus(order, "cancelled")}
                            >
                              {t("cancelled")}
                            </ResponsiveButton>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 3} unmountOnExit>
          <Card sx={{ ...cardSx, mb: 2 }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              <Grid container spacing={1.2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("search")}
                    value={filters.q}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, q: e.target.value }))
                    }
                    InputProps={{
                      endAdornment: filters.q ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setFilters((prev) => ({ ...prev, q: "" }))
                            }
                          >
                            ×
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ResponsiveButton
                    fullWidth
                    variant="contained"
                    onClick={openNewMapping}
                    sx={{
                      bgcolor: BRAND.orange,
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
                    }}
                  >
                    + {t("addMapping")}
                  </ResponsiveButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <SectionTitle
            title={t("mappings")}
            subtitle="Platform ürünlerini kendi stok/menü ürünlerinizle eşleştirin."
          />

          {mappings.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {mappings.map((mapping) => (
                <Grid item xs={12} md={6} xl={4} key={mapping.id}>
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                      <Stack spacing={1.2} sx={{ minWidth: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "flex-start" }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                overflowWrap: "anywhere",
                                lineHeight: 1.2,
                                fontSize: { xs: 18, md: 18 },
                              }}
                            >
                              {mapping.external_product_name}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 850,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {mapping.provider_name} • {mapping.account_name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={mapping.local_product_id ? "Mapped" : "Unmapped"}
                            color={mapping.local_product_id ? "success" : "warning"}
                            sx={{
                              fontWeight: 950,
                              alignSelf: { xs: "flex-start", sm: "center" },
                            }}
                          />
                        </Stack>

                        <Box
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            border: `1px solid ${BRAND.border}`,
                            minWidth: 0,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: BRAND.muted }}>
                            {t("localProductName")}
                          </Typography>

                          <Typography
                            sx={{
                              fontWeight: 950,
                              color: mapping.local_product_name
                                ? BRAND.blue
                                : BRAND.orange,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {mapping.local_product_name || t("unmappedProducts")}
                          </Typography>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={0.8}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            label={`${t("platformPrice")}: ${money(
                              mapping.platform_price,
                              mapping.currency,
                              lang
                            )}`}
                          />
                          <Chip size="small" label={mapping.currency || "TRY"} />
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <ResponsiveButton
                            fullWidth
                            variant="contained"
                            onClick={() => openEditMapping(mapping)}
                            sx={{
                              bgcolor: BRAND.blue,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: BRAND.blue2,
                                boxShadow: "none",
                              },
                            }}
                          >
                            {t("edit")}
                          </ResponsiveButton>

                          <ResponsiveButton
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={() => disableMapping(mapping)}
                          >
                            {t("disable")}
                          </ResponsiveButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 4} unmountOnExit>
          {filterBar}

          <SectionTitle
            title={t("reports")}
            subtitle="Platform, ülke ve para birimine göre gün sonu raporları."
            right={
              <ResponsiveButton
                fullWidth
                variant="outlined"
                onClick={exportReportsCsv}
                sx={{
                  borderColor: BRAND.orange,
                  color: BRAND.orange,
                }}
              >
                {t("exportCsv")}
              </ResponsiveButton>
            }
          />

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {reportSummary.length === 0 ? (
              <Grid item xs={12}>
                <EmptyBox text={t("noData")} />
              </Grid>
            ) : (
              reportSummary.map((summary) => (
                <Grid item xs={12} sm={6} lg={4} key={summary.currency}>
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                      <Typography
                        variant="body2"
                        sx={{ color: BRAND.muted, fontWeight: 900 }}
                      >
                        {t("reportSummary")} / {summary.currency}
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 950,
                          color: BRAND.blue,
                          mt: 0.6,
                          fontSize: { xs: 28, md: 30 },
                          overflowWrap: "anywhere",
                        }}
                      >
                        {money(summary.gross_total, summary.currency, lang)}
                      </Typography>

                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("orderCount")}
                          </Typography>
                          <Typography sx={{ fontWeight: 950 }}>
                            {summary.order_count}
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("netTotal")}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 950,
                              color: BRAND.green,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {money(summary.net_total, summary.currency, lang)}
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("commissionTotal")}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 950,
                              color: BRAND.orange,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {money(
                              summary.commission_total,
                              summary.currency,
                              lang
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {t("discountTotal")}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 950,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {money(summary.discount_total, summary.currency, lang)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>

          <SectionTitle title={t("dailyBreakdown")} />

          {reports.length === 0 ? (
            <EmptyBox text={t("noData")} />
          ) : (
            <Grid container spacing={1.5}>
              {reports.map((report, index) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={4}
                  key={`${report.report_date}-${report.provider_key}-${index}`}
                >
                  <Card sx={{ ...cardSx, height: "100%" }}>
                    <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                      <Stack spacing={1} sx={{ minWidth: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "flex-start" }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.text,
                                overflowWrap: "anywhere",
                                fontSize: { xs: 18, md: 18 },
                              }}
                            >
                              {report.provider_name || report.provider_key}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 850,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {formatDate(report.report_date, lang)} •{" "}
                              {report.country_code} / {report.currency}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={`${report.order_count} ${t("orderCount")}`}
                            sx={{
                              fontWeight: 950,
                              bgcolor: BRAND.softOrange,
                              color: BRAND.orange,
                              alignSelf: { xs: "flex-start", sm: "center" },
                            }}
                          />
                        </Stack>

                        <Divider />

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("grossTotal")}
                            </Typography>

                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.blue,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {money(report.gross_total, report.currency, lang)}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("netTotal")}
                            </Typography>

                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.green,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {money(report.net_total, report.currency, lang)}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("commissionTotal")}
                            </Typography>

                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BRAND.orange,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {money(
                                report.commission_total,
                                report.currency,
                                lang
                              )}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 800 }}
                            >
                              {t("cancelled")}
                            </Typography>

                            <Typography sx={{ fontWeight: 950, color: BRAND.red }}>
                              {report.cancelled_count || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>

        <Collapse in={tab === 5} unmountOnExit>
          <SectionTitle
            title={t("logsHelp")}
            subtitle="Entegrasyon hareketleri, test sonuçları ve hata kayıtları."
          />

          <Grid container spacing={1.5}>
            <Grid item xs={12} lg={7}>
              {logs.length === 0 ? (
                <EmptyBox text={t("noData")} />
              ) : (
                <Stack spacing={1.2}>
                  {logs.map((log) => (
                    <Card key={log.id} sx={cardSx}>
                      <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                        <Stack spacing={0.8} sx={{ minWidth: 0 }}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "flex-start" }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: BRAND.text,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {log.action || "-"}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: BRAND.muted,
                                  fontWeight: 850,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {log.provider_key || "-"} •{" "}
                                {formatDateTime(log.created_at, lang)}
                              </Typography>
                            </Box>

                            <Chip
                              size="small"
                              label={log.status}
                              color={STATUS_COLORS[log.status] || "default"}
                              sx={{
                                fontWeight: 950,
                                alignSelf: { xs: "flex-start", sm: "center" },
                              }}
                            />
                          </Stack>

                          <Typography
                            variant="body2"
                            sx={{
                              color: BRAND.muted,
                              lineHeight: 1.55,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {log.message || "-"}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={cardSx}>
                <CardContent
  sx={{
    pt: { xs: 2, md: 2 },
    pb: { xs: 2, md: 2 },
    pl: { xs: 3, md: 3 },
    pr: { xs: 3, md: 3 },
  }}
>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 950,
                      color: BRAND.text,
                      mb: 1,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {t("helpTitle")}
                  </Typography>

                  {[
                    [t("help1Title"), t("help1")],
                    [t("help2Title"), t("help2")],
                    [t("help3Title"), t("help3")],
                    [t("help4Title"), t("help4")],
                  ].map(([title, body]) => (
                    <Accordion
                      key={title}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: "14px !important",
                        mb: 1,
                        overflow: "hidden",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <span style={{ fontWeight: 900, fontSize: 18 }}>⌄</span>
                        }
                        sx={{
                          bgcolor: "#F8FAFC",
                          "& .MuiAccordionSummary-content": {
                            my: 1.2,
                            minWidth: 0,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 950,
                            color: BRAND.blue,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {title}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Typography
                          variant="body2"
                          sx={{
                            color: BRAND.muted,
                            lineHeight: 1.6,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {body}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Collapse>

        <Dialog
          open={accountDialog}
          onClose={() => setAccountDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4 },
          }}
        >
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.blue }}>
            {editingAccount ? t("edit") : t("addConnection")}
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "#F8FAFC" }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("platform")}
                  value={accountForm.provider_id}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      provider_id: e.target.value,
                      credentials: {},
                    }))
                  }
                  sx={inputSx}
                >
                  <MenuItem value="">{t("providerSelect")}</MenuItem>
                  {providers.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.provider_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("accountName")}
                  value={accountForm.account_name}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      account_name: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label={t("country")}
                  value={accountForm.country_code}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      country_code: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.code} - {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label={t("currency")}
                  value={accountForm.currency}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label={t("status")}
                  value={accountForm.status}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  <MenuItem value="active">{t("active")}</MenuItem>
                  <MenuItem value="inactive">{t("inactive")}</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("feePercent")}
                  value={accountForm.platform_fee_percent}
                  onChange={(e) =>
                    setAccountForm((prev) => ({
                      ...prev,
                      platform_fee_percent: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ fontWeight: 950, color: BRAND.blue, mb: 1 }}>
                  {t("apiSettings")}
                </Typography>
              </Grid>

              {selectedProviderFields.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    {t("providerSelect")}
                  </Alert>
                </Grid>
              ) : (
                selectedProviderFields.map((field) => (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      fullWidth
                      label={field}
                      value={accountForm.credentials?.[field] || ""}
                      onChange={(e) =>
                        setAccountForm((prev) => ({
                          ...prev,
                          credentials: {
                            ...(prev.credentials || {}),
                            [field]: e.target.value,
                          },
                        }))
                      }
                      sx={inputSx}
                    />
                  </Grid>
                ))
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ fontWeight: 950, color: BRAND.blue, mb: 1 }}>
                  {t("syncSettings")}
                </Typography>
              </Grid>

              {[
                ["orders", t("ordersSync")],
                ["menu", t("menuSync")],
                ["prices", t("pricesSync")],
                ["stock", t("stockSync")],
                ["cash", t("cashSync")],
              ].map(([key, label]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${BRAND.border}`,
                      boxShadow: "none",
                    }}
                  >
                    <CardContent sx={{ p: 1.3 }}>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          width: "100%",
                          justifyContent: "space-between",
                          "& .MuiFormControlLabel-label": {
                            fontWeight: 900,
                            overflowWrap: "anywhere",
                          },
                        }}
                        control={
                          <Switch
                            checked={Boolean(accountForm.sync_settings?.[key])}
                            onChange={(e) =>
                              setAccountForm((prev) => ({
                                ...prev,
                                sync_settings: {
                                  ...(prev.sync_settings || {}),
                                  [key]: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label={label}
                        labelPlacement="start"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setAccountDialog(false)}
              sx={{ borderRadius: 999, fontWeight: 950 }}
            >
              {t("cancel")}
            </Button>

            <ResponsiveButton
              variant="contained"
              onClick={saveAccount}
              sx={{
                bgcolor: BRAND.orange,
                boxShadow: "none",
                "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
              }}
            >
              {t("save")}
            </ResponsiveButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={orderDialog}
          onClose={() => setOrderDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4 },
          }}
        >
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.blue }}>
            {t("addManualOrder")}
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "#F8FAFC" }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("connections")}
                  value={orderForm.account_id}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      account_id: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.account_name} - {account.provider_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("externalOrderId")}
                  value={orderForm.external_order_id}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      external_order_id: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("orderNo")}
                  value={orderForm.order_no}
                  onChange={(e) =>
                    setOrderForm((prev) => ({ ...prev, order_no: e.target.value }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("customerName")}
                  value={orderForm.customer_name}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      customer_name: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("grossAmount")}
                  value={orderForm.gross_amount}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      gross_amount: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("discountAmount")}
                  value={orderForm.discount_amount}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      discount_amount: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("commissionAmount")}
                  value={orderForm.commission_amount}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      commission_amount: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("netAmount")}
                  value={orderForm.net_amount}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      net_amount: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label={t("orderDate")}
                  value={orderForm.order_date}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      order_date: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("paymentMethod")}
                  value={orderForm.payment_method}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      payment_method: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("deliveryType")}
                  value={orderForm.delivery_type}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      delivery_type: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label={t("items")}
                  helperText={t("itemHelp")}
                  value={orderForm.items_text}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      items_text: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setOrderDialog(false)}
              sx={{ borderRadius: 999, fontWeight: 950 }}
            >
              {t("cancel")}
            </Button>

            <ResponsiveButton
              variant="contained"
              onClick={saveManualOrder}
              sx={{
                bgcolor: BRAND.orange,
                boxShadow: "none",
                "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
              }}
            >
              {t("save")}
            </ResponsiveButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={mappingDialog}
          onClose={() => setMappingDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4 },
          }}
        >
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.blue }}>
            {editingMapping ? t("edit") : t("addMapping")}
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "#F8FAFC" }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label={t("connections")}
                  value={mappingForm.account_id}
                  onChange={(e) => {
                    const acc = accounts.find(
                      (account) => String(account.id) === String(e.target.value)
                    );

                    setMappingForm((prev) => ({
                      ...prev,
                      account_id: e.target.value,
                      currency: acc?.currency || prev.currency || "TRY",
                    }));
                  }}
                  sx={inputSx}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.account_name} - {account.provider_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("externalProductId")}
                  value={mappingForm.external_product_id}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      external_product_id: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("externalProductName")}
                  value={mappingForm.external_product_name}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      external_product_name: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("externalVariantName")}
                  value={mappingForm.external_variant_name}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      external_variant_name: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("localProductId")}
                  value={mappingForm.local_product_id}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      local_product_id: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label={t("localProductName")}
                  value={mappingForm.local_product_name}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      local_product_name: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label={t("platformPrice")}
                  value={mappingForm.platform_price}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      platform_price: e.target.value,
                    }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label={t("currency")}
                  value={mappingForm.currency}
                  onChange={(e) =>
                    setMappingForm((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  sx={inputSx}
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setMappingDialog(false)}
              sx={{ borderRadius: 999, fontWeight: 950 }}
            >
              {t("cancel")}
            </Button>

            <ResponsiveButton
              variant="contained"
              onClick={saveMapping}
              sx={{
                bgcolor: BRAND.orange,
                boxShadow: "none",
                "&:hover": { bgcolor: "#EA580C", boxShadow: "none" },
              }}
            >
              {t("save")}
            </ResponsiveButton>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
            sx={{ borderRadius: 3, fontWeight: 800 }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}